/**
 * @fileoverview Connect Intake/matter subjects to saved conflict searches and human hit review. The backend owns matching, evidence, closure requirements, and access; this workspace owns search additions and explicit dispositions. `kc-internal-backend/services/conflict_checks/__init__.py` maps the area. Search hits inform staff review rather than automatically establishing conflict clearance.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';
import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppSelect, AppSpinner, AppTextarea, AppTextInput } from '../shared';
import AppIcon from '../shared/AppIcon';
import './ConflictCheckPanel.css';

export const REVIEW_OPTIONS = [
  { value: 'unreviewed', label: 'Needs review' },
  { value: 'cleared', label: 'Cleared' },
  { value: 'potential_conflict', label: 'Potential conflict' },
  { value: 'conflict', label: 'Conflict' },
];

const ROLE_OPTIONS = [
  { value: 'client', label: 'Client' },
  { value: 'adverse_party', label: 'Adverse party' },
  { value: 'opposing_counsel', label: 'Opposing counsel' },
];

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS.map((o) => [o.value, o.label]));

const OUTCOME_TONE = {
  cleared: 'success',
  potential_conflict: 'warning',
  conflict: 'danger',
  unreviewed: 'neutral',
};

 
function Pill({ tone = 'neutral', icon, children }) {
  return (
    <span className={`conflict-checks__pill conflict-checks__pill--${tone}`}>
      {icon ? <AppIcon icon={icon} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

 
export function outcomeLabel(value) {
  return (REVIEW_OPTIONS.find((o) => o.value === value) || {}).label || 'Open';
}

 
function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

 
function errorMessage(error, fallback) {
  return error?.response?.data?.error || fallback;
}



function ConflictCheckPanel({ subjectType, leadId, matterNumber, active = true }) {
  const showToast = useContext(ToastContext);
  const basePath = subjectType === 'lead'
    ? `/intake/leads/${leadId}/conflict-checks`
    : `/matter/${encodeURIComponent(matterNumber || '')}/conflict-checks`;
  const [summary, setSummary] = useState(null);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [extraTerms, setExtraTerms] = useState([]);
  const [extraRole, setExtraRole] = useState('adverse_party');
  const [extraName, setExtraName] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [includeSameSide, setIncludeSameSide] = useState(false);

   
  const notify = useCallback((severity, detail) => {
    if (typeof showToast === 'function') {
      showToast({ severity, summary: severity === 'error' ? 'Error' : 'Conflict check', detail });
    }
  }, [showToast]);

   
  const loadCheck = useCallback(async (checkId) => {
    const response = await api.get(`/conflict-checks/${checkId}`);
    setCurrent(response.data);
  }, []);

   
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(basePath);
      setSummary(response.data);
      const latest = (response.data?.checks || [])[0];
      if (latest) {
        await loadCheck(latest.id);
      } else {
        setCurrent(null);
      }
    } catch (error) {
      notify('error', errorMessage(error, 'Could not load conflict checks.'));
    } finally {
      setLoading(false);
    }
  }, [basePath, loadCheck, notify]);

  useEffect(() => {
    if (active && (leadId || matterNumber)) load();
  }, [active, leadId, matterNumber, load]);

   
  const runCheck = async () => {
    setRunning(true);
    try {
      const response = await api.post(basePath, { extra_terms: extraTerms, include_same_side: includeSameSide });
      setCurrent(response.data);
      setExtraTerms([]);
      notify('success', `Search finished with ${response.data.hit_count} hit(s).`);
      const list = await api.get(basePath);
      setSummary(list.data);
    } catch (error) {
      notify('error', errorMessage(error, 'The conflict search failed.'));
    } finally {
      setRunning(false);
    }
  };

   
  const addExtraTerm = () => {
    const name = extraName.trim();
    if (!name) return;
    setExtraTerms((terms) => [...terms, { role: extraRole, name }]);
    setExtraName('');
  };

   
  const reviewHit = async (hit, reviewStatus, reviewNote = hit.review_note) => {
    try {
      const response = await api.patch(`/conflict-checks/${current.id}/hits/${hit.id}`, {
        review_status: reviewStatus,
        review_note: reviewNote,
      });
      setCurrent((check) => ({
        ...check,
        hits: check.hits.map((row) => (row.id === hit.id ? response.data : row)),
      }));
    } catch (error) {
      notify('error', errorMessage(error, 'Could not save the hit status.'));
    }
  };

   
  const closeCheck = async () => {
    try {
      const response = await api.post(`/conflict-checks/${current.id}/close`, { notes: closeNotes });
      setCurrent(response.data);
      setCloseNotes('');
      notify('success', `Check closed: ${outcomeLabel(response.data.outcome)}.`);
      const list = await api.get(basePath);
      setSummary(list.data);
    } catch (error) {
      notify('error', errorMessage(error, 'Could not close the check.'));
    }
  };

  const pendingCount = current ? current.hits.filter((hit) => hit.review_status === 'unreviewed').length : 0;
  const isOpen = current?.status === 'open';

  const latestOutcome = summary?.latest_outcome;

  return (
    <div className="conflict-checks" data-testid="conflict-check-panel">
      <section className="conflict-checks__section" aria-label="Run a conflict check">
        <header className="conflict-checks__header">
          <div>
            <h3 className="conflict-checks__title">Conflict check</h3>
            <p className="conflict-checks__subtitle">
              Searches clients, adverse parties, opposing counsel, and intake/Pipedrive contacts with exact and fuzzy matching.
            </p>
          </div>
          <div className="conflict-checks__header-actions">
            {latestOutcome
              ? <Pill tone={OUTCOME_TONE[latestOutcome]} icon="pi pi-shield">Last closed: {outcomeLabel(latestOutcome)}</Pill>
              : <Pill icon="pi pi-clock">No closed check</Pill>}
            <AppButton label={running ? 'Searching…' : 'Run new check'} icon="pi pi-search" onClick={runCheck} disabled={running || loading} />
          </div>
        </header>

        <span className="conflict-checks__label">Additional names (optional)</span>
        <div className="conflict-checks__add">
          <AppSelect id="conflict-extra-role" value={extraRole} options={ROLE_OPTIONS} onChange={setExtraRole} />
          <AppTextInput
            id="conflict-extra-name"
            value={extraName}
            placeholder="Add another name to search"
            onChange={(event) => setExtraName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') addExtraTerm(); }}
          />
          <AppButton label="Add name" icon="pi pi-plus" variant="secondary" onClick={addExtraTerm} disabled={!extraName.trim()} />
        </div>
        {extraTerms.length > 0 && (
          <div className="conflict-checks__chips">
            {extraTerms.map((term, index) => (
              <Pill key={`${term.role}-${term.name}-${index}`} tone="info">{ROLE_LABELS[term.role]}: {term.name}</Pill>
            ))}
          </div>
        )}
        <label className="conflict-checks__checkbox" htmlFor="conflict-include-same-side">
          <input
            id="conflict-include-same-side"
            type="checkbox"
            checked={includeSameSide}
            onChange={(event) => setIncludeSameSide(event.target.checked)}
          />
          Also show same-side matches (e.g. an adverse party the firm has sued before). These are usually not conflicts.
        </label>
      </section>

      {loading && !current && <div className="conflict-checks__loading"><AppSpinner /></div>}

      {!loading && !current && summary && (
        <p className="conflict-checks__empty">No conflict check has been run yet.</p>
      )}

      {current && (
        <section className="conflict-checks__section" aria-label="Current conflict check">
          <header className="conflict-checks__header">
            <div>
              <h3 className="conflict-checks__title">{isOpen ? 'Open check' : `Closed: ${outcomeLabel(current.outcome)}`}</h3>
              <p className="conflict-checks__subtitle">
                Run {formatDate(current.created_at)}{current.created_by_email ? ` by ${current.created_by_email}` : ''}
                {current.closed_at ? ` · Closed ${formatDate(current.closed_at)} by ${current.closed_by_email || 'unknown'}` : ''}
              </p>
            </div>
            <div className="conflict-checks__header-actions">
              {isOpen
                ? <Pill tone={pendingCount ? 'info' : 'success'}>{pendingCount} to review</Pill>
                : <Pill tone={OUTCOME_TONE[current.outcome]}>{outcomeLabel(current.outcome)}</Pill>}
            </div>
          </header>

          <div className="conflict-checks__field">
            <span className="conflict-checks__label">Names searched</span>
            <div className="conflict-checks__chips">
              {(current.search_terms || []).map((term) => (
                <Pill key={`${term.role}-${term.name}`}>{ROLE_LABELS[term.role] || term.role}: {term.name}</Pill>
              ))}
            </div>
          </div>

          {current.hits.length === 0 ? (
            <p className="conflict-checks__empty">No matching names found.</p>
          ) : (
            <ul className="conflict-checks__hits">
              {current.hits.map((hit) => (
                <li key={hit.id} className="conflict-checks__hit" data-testid={`conflict-hit-${hit.id}`}>
                  <div>
                    <span className="conflict-checks__label">{ROLE_LABELS[hit.term_role]}</span>
                    <div className="conflict-checks__name">{hit.term_name}</div>
                  </div>
                  <div>
                    <div className="conflict-checks__name">{hit.matched_name}</div>
                    <div className="conflict-checks__note">{hit.relationship_note}</div>
                    <div className="conflict-checks__related">
                      <Pill tone={hit.match_type === 'exact' ? 'danger' : 'warning'}>
                        {hit.match_type === 'exact' ? 'Exact match' : `Fuzzy ${Math.round((hit.score || 0) * 100)}%`}
                      </Pill>
                      {(hit.related_matters || []).map((matter) => (
                        <a key={matter} href={`/case_details/${encodeURIComponent(matter)}`}>{matter}</a>
                      ))}
                      {hit.related_lead_id && !(hit.related_matters || []).length ? <span>Lead #{hit.related_lead_id}</span> : null}
                      {hit.pipedrive_person_id ? <span className="conflict-checks__meta">Pipedrive person {hit.pipedrive_person_id}</span> : null}
                    </div>
                  </div>
                  <div className="conflict-checks__review">
                    {isOpen ? (
                      <>
                        <AppSelect
                          id={`conflict-hit-status-${hit.id}`}
                          value={hit.review_status}
                          options={REVIEW_OPTIONS}
                          onChange={(value) => reviewHit(hit, value)}
                        />
                        <AppTextInput
                          id={`conflict-hit-note-${hit.id}`}
                          value={hit.review_note || ''}
                          placeholder="Reason (optional)"
                          onChange={(event) => {
                            const note = event.target.value;
                            setCurrent((check) => ({
                              ...check,
                              hits: check.hits.map((row) => (row.id === hit.id ? { ...row, review_note: note } : row)),
                            }));
                          }}
                          onBlur={() => hit.review_status !== 'unreviewed' && reviewHit(hit, hit.review_status, hit.review_note)}
                        />
                      </>
                    ) : (
                      <>
                        <div><Pill tone={OUTCOME_TONE[hit.review_status]}>{outcomeLabel(hit.review_status)}</Pill></div>
                        {hit.review_note ? <div className="conflict-checks__note">{hit.review_note}</div> : null}
                        {hit.reviewed_by_email ? <div className="conflict-checks__meta">{hit.reviewed_by_email}</div> : null}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {isOpen && (
            <div className="conflict-checks__close">
              <span className="conflict-checks__label">Closing notes (optional)</span>
              <AppTextarea id="conflict-close-notes" value={closeNotes} onChange={setCloseNotes} rows={2} />
              <div className="conflict-checks__close-actions">
                <AppButton
                  label="Close check"
                  icon="pi pi-check"
                  onClick={closeCheck}
                  disabled={pendingCount > 0}
                />
                {pendingCount > 0 ? <span className="conflict-checks__meta">Give every hit a status before closing.</span> : null}
              </div>
            </div>
          )}
          {!isOpen && current.notes ? <p className="conflict-checks__note">Notes: {current.notes}</p> : null}
        </section>
      )}

      {(summary?.checks || []).length > 1 && (
        <section className="conflict-checks__section" aria-label="Conflict check history">
          <header className="conflict-checks__header">
            <h3 className="conflict-checks__title">History</h3>
          </header>
          <ul className="conflict-checks__history">
            {summary.checks.map((check) => (
              <li key={check.id} className={`conflict-checks__history-row${current?.id === check.id ? ' is-selected' : ''}`}>
                <Pill tone={check.status === 'open' ? 'info' : OUTCOME_TONE[check.outcome]}>
                  {check.status === 'open' ? 'Open' : outcomeLabel(check.outcome)}
                </Pill>
                <span>{formatDate(check.created_at)} · {check.subject_type === 'lead' ? 'Intake' : 'Matter opening'} · {check.hit_count} hit(s)</span>
                <AppButton label="View" icon="pi pi-eye" variant="secondary" onClick={() => loadCheck(check.id)} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default ConflictCheckPanel;
